import {
  type ComponentPropsWithoutRef,
  memo,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';

import { type Annotation, type Clip, type CommentResponse } from '@air/api/types';
import { type DiscussionsCommentProps, DiscussionsCommentV2 } from '@air/component-discussions';
import { formatFullName, formatUpdatedAt, parseAirDateToISO } from '@air/utilities';
import { useCurrentUserSession } from '@air/utils-auth';

import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import { CommentAvatar } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/CommentAvatar';
import {
  CommentMenu,
  type CommentMenuProps,
} from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/CommentMenu';
import { DiscussionCommentContent } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/DiscussionCommentContent';
import { DiscussionVersionNumber } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/DiscussionVersionNumber';
import { ReopenDiscussionButton } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/ReopenDiscussionButton';
import { useCommentEditing } from '~/components/DiscussionsV2/Discussion/shared/useCommentEditing';
import { type MentionsData } from '~/components/DiscussionsV2/DiscussionForm';
import { useDiscussionPermissions } from '~/components/DiscussionsV2/providers/DiscussionPermissionsProvider';
import { useAccountContext } from '~/providers/AccountProvider';
import { isCommentAuthor } from '~/utils/comments';
import { canEditAssetDiscussions } from '~/utils/permissions/assetPermissions';

export type DiscussionCommentProps = Pick<DiscussionsCommentProps, 'className' | 'secondaryActions'> &
  Pick<ComponentPropsWithoutRef<'div'>, 'style'> & {
    discussion: ClipDiscussion;
    comment: CommentResponse;

    onSave?: (params: {
      newComment: string;
      comment: CommentResponse;
      clipId: Clip['id'];
      timestamp?: number;
      annotation?: Annotation;
    }) => void;

    onDelete?: CommentMenuProps['onDelete'];
    onToggleResolve?: CommentMenuProps['onToggleResolve'];

    currentVersion?: number;

    onSwitchVersion?: ((clipId: string) => void) | undefined;
    mentionsList: MentionsData[];
    isLoadingMentions?: boolean;
    shouldShowMentionsButton?: boolean;

    commentMetadata?: ReactNode;
    commentMetadataOnOwnLine?: boolean;
    showAvatar?: boolean;
  };

export const DiscussionComment = memo(
  ({
    discussion,
    className,
    comment,
    onSwitchVersion,
    isLoadingMentions,
    mentionsList,
    shouldShowMentionsButton,
    style,
    currentVersion,
    onSave,
    onDelete,
    onToggleResolve,
    commentMetadata,
    commentMetadataOnOwnLine,
    secondaryActions,
  }: PropsWithChildren<DiscussionCommentProps>) => {
    const { disableEditing, enableEditing, isEditing } = useCommentEditing();
    const { data: account } = useAccountContext();
    const { data: userSession } = useCurrentUserSession();
    const isTopLevelComment = discussion.comments.data[0].id === comment.id;
    const isAuthor = isCommentAuthor({ comment, account, userSession });
    const { permissions } = useDiscussionPermissions();

    const canResolve = isTopLevelComment && onToggleResolve && canEditAssetDiscussions(permissions);
    const canEdit = onSave && isAuthor && canEditAssetDiscussions(permissions);
    const canDelete = onDelete && isAuthor && canEditAssetDiscussions(permissions);

    const commentMenu = useMemo(
      () =>
        discussion.resolved && isTopLevelComment && canResolve ? null : ( // Re-open button moved to secondaryActions when resolved
          <CommentMenu
            comment={comment}
            discussion={discussion}
            onToggleResolve={canResolve ? onToggleResolve : undefined}
            onDelete={canDelete ? onDelete : undefined}
            onEdit={canEdit ? enableEditing : undefined}
          />
        ),
      [
        discussion,
        isTopLevelComment,
        canResolve,
        onToggleResolve,
        comment,
        canDelete,
        onDelete,
        canEdit,
        enableEditing,
      ],
    );

    const reopenButton = useMemo(
      () =>
        discussion.resolved && isTopLevelComment && canResolve ? (
          <ReopenDiscussionButton onToggleResolve={onToggleResolve} discussion={discussion} />
        ) : null,
      [discussion, isTopLevelComment, canResolve, onToggleResolve],
    );

    const finalSecondaryActions = useMemo(() => {
      if (reopenButton && secondaryActions) {
        return (
          <>
            {reopenButton}
            {secondaryActions}
          </>
        );
      }

      return reopenButton || secondaryActions;
    }, [reopenButton, secondaryActions]);

    const commentAvatar = useMemo(
      () => <CommentAvatar account={comment.account} isResolved={discussion.resolved} />,
      [comment.account, discussion.resolved],
    );

    const onSaveClick = useCallback(
      (newComment: string) => {
        onSave?.({
          newComment,
          comment,
          clipId: discussion.clipId,
          timestamp: discussion.timestamp,
          annotation: discussion.annotation,
        });
      },
      [onSave, comment, discussion],
    );

    const headerVersion = useMemo(
      () =>
        onSwitchVersion && isTopLevelComment && discussion.assetVersion ? (
          <DiscussionVersionNumber
            discussionVersion={discussion.assetVersion}
            onVersionClick={
              discussion.assetVersion !== currentVersion
                ? () => {
                    onSwitchVersion?.(discussion.clipId);
                  }
                : undefined
            }
          />
        ) : null,
      [currentVersion, discussion.assetVersion, discussion.clipId, isTopLevelComment, onSwitchVersion],
    );

    return (
      <DiscussionsCommentV2
        actions={commentMenu}
        author={formatFullName(comment.account.firstName, comment.account.lastName)}
        avatar={commentAvatar}
        className={className}
        comment={
          <DiscussionCommentContent
            key={comment.id}
            isEditing={isEditing}
            cancelEditing={disableEditing}
            comment={comment}
            onSave={canEdit ? onSaveClick : undefined}
            isLoadingMentions={isLoadingMentions}
            mentionsList={mentionsList}
            shouldShowMentionsButton={shouldShowMentionsButton}
          />
        }
        commentMetadata={commentMetadata}
        commentMetadataOnOwnLine={commentMetadataOnOwnLine}
        data-comment-id={comment.id}
        meta={headerVersion}
        secondaryActions={finalSecondaryActions}
        style={style}
        timestamp={formatUpdatedAt(parseAirDateToISO(comment.createdAt).getTime())}
      />
    );
  },
);

DiscussionComment.displayName = 'DiscussionComment';
